import React, { useState } from 'react';
import { DatePicker as DatePickerMui } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import { Caption, Theme } from '@routific/hive';
import { ThemeProvider, Box } from '@mui/material';
import muiFormTheme from '../Form/muiFormTheme';
import CalendarIcon from './CalendarIcon';
import TextInput from './TextInput';
import PreviousIcon from '../DatePickerCalendar/PreviousIcon';
import NextIcon from '../DatePickerCalendar/NextIcon';
import SwitchViewIcon from '../DatePickerCalendar/SwitchViewIcon';
import Day from '../DatePickerCalendar/Day';
import DatePickerActionBar from './DatePickerActionBar';
import { DateValidationError } from '@mui/x-date-pickers';
import { getSlotProps, SlotPropsParams } from './utils';

export enum DatePickerType {
  START = 'start',
  END = 'end',
}
interface DatePickerProps {
  label?: string;
  type?: DatePickerType;
  value?: DateTime | null;
  onChange: (newValue?: DateTime | null) => void;
  onOpen: () => void;
  onClose: () => void;
  onAccept?: (newValue?: DateTime | null) => void;
  onMonthChange?: (newValue: DateTime) => void;
  onYearChange?: (newValue: DateTime) => void;
  minDate?: DateTime;
  maxDate?: DateTime;
  open: boolean;
  required?: boolean;
  onJumpToToday?: () => void;
  disableJumpToToday?: boolean | null;
  rangeStart?: DateTime | null;
  rangeEnd?: DateTime | null;
  onError?: (err: DateValidationError) => void;
  hasError?: boolean;
  dayHasRoutes?: (dayToCheck: any) => boolean,
  placeholder?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  onOpen,
  onMonthChange,
  onYearChange,
  onClose,
  onAccept,
  onError,
  hasError,
  minDate,
  maxDate,
  open,
  required = false,
  rangeStart,
  rangeEnd,
  type,
  dayHasRoutes,
  placeholder,
}) => {
  const [isFocused, setIsFocused] = useState(false); // State to track focus
  const [hoveredDay, setHoveredDay] = React.useState<DateTime | null>(null);

  const handleOpenPicker = () => {
    setIsFocused(true);
    onOpen();
  };

  const handleClosePicker = () => {
    setIsFocused(false);
    setHoveredDay(null);
    onClose();
  };

  const theme = muiFormTheme;

  const getStyledInputLabel = () => {
    if (!label) return null;

    return (<Box display='flex' style={{ marginBottom: Theme.Spacing.smSpacing }}>
      <Caption type={isFocused ? 'accent' : 'secondary'}>{label}</Caption>
      {required && (
        <Caption type="danger" style={{ marginLeft: 2 }}>
          *
        </Caption>
      )}
    </Box>);
  };

  return (
    <Box width="100%" display={'flex'} flexDirection={'column'}>
      {getStyledInputLabel()}
      <ThemeProvider theme={theme}>
        <DatePickerMui
          value={value}
          onChange={onChange}
          onError={onError}
          onOpen={handleOpenPicker}
          onMonthChange={onMonthChange}
          onYearChange={onYearChange}
          onClose={handleClosePicker}
          onAccept={onAccept}
          minDate={minDate}
          maxDate={maxDate}
          desktopModeMediaQuery='@media (min-width:0px)'
          dayOfWeekFormatter={(day) => day.toFormat('ccc').slice(0, 2)
          }
          open={open}
          slots={{
            openPickerIcon: CalendarIcon,
            textField: TextInput,
            day: Day,
            leftArrowIcon: PreviousIcon,
            rightArrowIcon: NextIcon,
            switchViewIcon: SwitchViewIcon,
            actionBar: DatePickerActionBar,
          }}
          slotProps={
            getSlotProps({
              hoveredDay,
              setHoveredDay,
              onChange,
              isFocused,
              setIsFocused,
              rangeStart,
              rangeEnd,
              value,
              type,
              hasError,
              dayHasRoutes,
              placeholder,
            } as SlotPropsParams)}
        />
      </ThemeProvider>
    </Box>
  );
};

export default DatePicker;
